import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ApiError, checkoutApi } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { OwlPostBadge } from '@/components/OwlPostBadge';
import { PriceDisplay } from '@/components/PriceDisplay';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

export function OrderDetail() {
  const { orderId = '' } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const order = useQuery({
    queryKey: ['order', orderId],
    queryFn: async () => (await checkoutApi.order(orderId)).data,
    enabled: !!orderId,
  });

  // A direct/bookmarked order link has no `me` gate up front (unlike
  // Checkout/Orders). checkout now 401s an anonymous or session-expired
  // fetch here (it used to 500) — bounce to login with a return-to
  // rather than showing "Order not found", which is misleading (the
  // order may well exist; the visitor just isn't authenticated for it).
  const unauthorized = order.error instanceof ApiError && order.error.status === 401;
  useEffect(() => {
    if (unauthorized) navigate(`/login?next=${encodeURIComponent(`/orders/${orderId}`)}`, { replace: true });
  }, [unauthorized, orderId, navigate]);

  if (order.isLoading || unauthorized) return <Skeleton className="h-64" />;
  if (!order.data) {
    return <p className="text-(--color-ink-muted)">Order not found. <Link to="/orders" className="underline">Back to orders</Link></p>;
  }

  const o = order.data;
  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <header className="text-center space-y-3">
        <p className="text-(--color-ink-muted)">Owl is on its way</p>
        <OwlPostBadge orderId={o.id} className="text-xl" />
        <Badge variant={o.status === 'fulfilled' ? 'gold' : 'secondary'}>{o.status}</Badge>
      </header>

      <Separator />

      <ul className="divide-y rounded-lg border bg-card">
        {o.items.map((it) => (
          <li key={it.productId} className="p-4 flex items-center gap-4">
            <div className="flex-1">
              <p className="font-medium">{it.name}</p>
              <p className="text-sm text-(--color-ink-muted)">×{it.quantity}</p>
            </div>
            <PriceDisplay knuts={it.unit_price_knuts * it.quantity} />
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between">
        <span className="font-display text-xl">Total</span>
        <PriceDisplay knuts={o.total_knuts} className="text-2xl" />
      </div>

      <p className="text-sm text-(--color-ink-muted)">
        Payment: {o.payment_outcome} · Placed {new Date(o.placed_at).toLocaleString()}
      </p>
    </div>
  );
}
